import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Navbar from './components/Navbar';
import Home from './components/Home';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AppMetadata from './components/AppMetadata';
import IntroOverlay from './components/IntroOverlay';
import MobileCartShortcut from './components/MobileCartShortcut';

import InstallAppPrompt from './components/InstallAppPrompt';
import { useCart } from './context/CartContext';
import { PublicExperienceProvider, usePublicExperience } from './context/PublicExperienceContext';
import { MenuDataProvider, useMenuData } from './context/MenuDataContext';

const Menu = lazy(() => import('./components/Menu'));
const Carrinho = lazy(() => import('./components/Carrinho'));
const ConfiguredPage = lazy(() => import('./components/publicExperience/ConfiguredPage'));

function AppShell() {
  const location = useLocation();
  const { cartItems } = useCart();
  const { loadingExperience } = usePublicExperience();
  const { loadingMenu } = useMenuData();
  const isHomeRoute = location.pathname === '/';
  const isMenuRoute = location.pathname === '/cardapio';
  const totalCartItems = cartItems.reduce(
    (total, item) => total + Math.max(1, Number(item.quantity) || 1),
    0,
  );
  const shouldPrioritizeCartShortcut = isMenuRoute && totalCartItems > 0;
  const [introDismissed, setIntroDismissed] = useState(() => !isHomeRoute);
  const dismissIntro = useCallback(() => setIntroDismissed(true), []);
  const showIntro = isHomeRoute && !introDismissed;

  useEffect(() => {
    if (!isHomeRoute) dismissIntro();
  }, [dismissIntro, isHomeRoute]);

  return (
    <>
      <div
        className={`App ${isHomeRoute ? 'App--home' : ''} ${
          shouldPrioritizeCartShortcut ? 'App--cart-shortcut-route' : ''
        }`}
        inert={showIntro ? true : undefined}
        aria-hidden={showIntro ? 'true' : undefined}
      >
        <ScrollToTop />
        <AppMetadata />

        <Navbar />

        <main className="AppMain" role="main">
          <Suspense
            fallback={
              <div className="route-loading" role="status" aria-live="polite">
                Carregando...
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/cardapio" element={<Menu />} />
              <Route path="/carrinho" element={<Carrinho />} />
              <Route path="/:slug" element={<ConfiguredPage />} />
            </Routes>
          </Suspense>
        </main>

        <MobileCartShortcut itemCount={totalCartItems} />
        <InstallAppPrompt cartItemsCount={cartItems.length} />
        <Footer />

        <ToastContainer
          position="bottom-right"
          autoClose={3500}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={3}
        />
      </div>
      {showIntro && (
        <IntroOverlay ready={!loadingExperience && !loadingMenu} onComplete={dismissIntro} />
      )}
    </>
  );
}

function App() {
  return (
    <PublicExperienceProvider>
      <MenuDataProvider>
        <AppShell />
      </MenuDataProvider>
    </PublicExperienceProvider>
  );
}

export default App;
